import { Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import type { RichTooltipProps } from 'components/RichTooltip';
import React from 'react';
import type { BulletPattern, BulletVariant } from './BulletIndicator';
import BulletIndicator from './BulletIndicator';
import styles from './DataTableTooltip.module.scss';

type ValueAlign = 'left' | 'right';

export type DataTableTooltipDataItem = {
  label: React.ReactNode;
  value?: React.ReactNode;
  secondaryValue?: React.ReactNode;
  /** Applies line-through style on secondary value */
  secondaryValueIsOutdated?: boolean;
  align?: ValueAlign;
  bulletVariant?: BulletVariant;
  bulletPattern?: BulletPattern;
  subValues?: Array<Omit<DataTableTooltipDataItem, 'subValues'>>;
  mask?: boolean;
  header?: boolean;
};

/** String creates a heading */
export type TableDataValue = DataTableTooltipDataItem | 'divider' | string | React.ReactElement;

/** Props that are passed to DataTooltip */
interface PassedProps {
  dataValues: Array<TableDataValue> | (() => Array<TableDataValue>);
  title?: string;
  subTitle?: string;
  /** Disabling the tooltip will directly render the `children */
  disabled?: boolean;
  isOpen?: boolean;
  footer?: React.ReactElement;
  labelsClassName?: string;
}

interface Props
  extends Omit<RichTooltipProps, 'tooltip'>,
    PassedProps,
    Omit<React.HTMLProps<HTMLDivElement>, 'onClick'> {}

const DataTableTooltip = (props: Props) => {
  const {
    children,
    title,
    subTitle,
    footer,
    position = 'top',
    disabled,
    dataValues,
    labelsClassName,
    ...rest
  } = props;

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      {...rest}
      position={position}
      tooltip={
        <DataTable
          className={styles.tooltipContainer}
          title={title}
          subTitle={subTitle}
          footer={footer}
          labelsClassName={labelsClassName}
          dataValues={dataValues}
        />
      }
    >
      {children}
    </Tooltip>
  );
};

export default DataTableTooltip;

interface DataTableProps extends PassedProps, React.HTMLProps<HTMLDivElement> {}

/**
 * The grid layout here ought to be abstracted out to real <Grid />, so many ~same implementations around for all basic layouts
 */
export const DataTable = (props: DataTableProps) => {
  const { title, subTitle, dataValues, children, footer, labelsClassName, ...rest } = props;

  const dataValuesArray = React.useMemo(() => {
    if (dataValues instanceof Function) {
      return dataValues();
    }
    return dataValues;
  }, [dataValues]);

  return (
    <div {...rest}>
      {!!title && <h5 className={styles.header}>{title}</h5>}
      {!!subTitle && <h6 className={styles.subTitle}>{subTitle}</h6>}
      <div className={classNames(styles.grid, labelsClassName)}>
        {dataValuesArray.map((dataValue, idx) => {
          if (dataValue === 'divider') {
            return <div key={idx} className={styles.divider} />;
          }

          if (React.isValidElement<any>(dataValue)) {
            return (
              <div className={styles.fullrow} key={idx}>
                {dataValue}
              </div>
            );
          }

          // Header
          if (typeof dataValue === 'string' || dataValue.header) {
            return (
              <div key={idx} className={styles.header}>
                {typeof dataValue === 'string' ? dataValue : dataValue.label}
              </div>
            );
          }

          // Give more space to a single value, improving alignment of secondary values elsewhere
          const onlyHasValue = dataValue.value != null && dataValue.secondaryValue == null;

          // DataItem
          return (
            <React.Fragment key={idx}>
              <div className={styles.label}>
                {!!dataValue.bulletVariant && (
                  <BulletIndicator
                    variant={dataValue.bulletVariant}
                    pattern={dataValue.bulletPattern}
                    size="small"
                  />
                )}
                {dataValue.label}
              </div>
              {!onlyHasValue && (
                <div
                  className={classNames(
                    styles.secondaryValue,
                    styles[`value-align-${dataValue.align}`],
                    {
                      [styles.outdated]: dataValue.secondaryValueIsOutdated,
                      'fs-mask': dataValue.mask,
                    }
                  )}
                >
                  {dataValue.secondaryValue}
                </div>
              )}
              <div
                className={classNames(styles.value, styles[`value-align-${dataValue.align}`], {
                  [styles.onlyHasValue]: onlyHasValue,
                  'fs-mask': dataValue.mask,
                })}
              >
                {dataValue.value}
              </div>
              {/*Indented sub-items*/}
              {dataValue.subValues?.map((subValue, i) => (
                <React.Fragment key={i}>
                  <div
                    style={{ marginLeft: 18 }}
                    className={classNames(styles.label, styles.subValue, {
                      'fs-mask': dataValue.mask,
                    })}
                  >
                    <BulletIndicator
                      variant={subValue?.bulletVariant}
                      pattern={subValue?.bulletPattern}
                      size="small"
                    />
                    {subValue.label}
                  </div>
                  <div
                    className={classNames(
                      styles.secondaryValue,
                      styles[`value-align-${subValue.align}`]
                    )}
                  >
                    {subValue.secondaryValue}
                  </div>
                  <div
                    className={classNames(styles.value, styles[`value-align-${subValue.align}`])}
                  >
                    {subValue.value}
                  </div>
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        })}
      </div>
      {!!footer && <div className={styles.footer}>{footer}</div>}
      {children}
    </div>
  );
};
